import type { Action, ActionStatus, DeskToolPermission, ToolPermission } from '@deskworks/shared-types';
import { TOOL_PERMISSIONS } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { ActionArguments } from '@/components/actions/action-arguments';
import { JsonBlock } from '@/components/code-block';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isPersonTask, PersonTaskCard } from '@/components/person-task-card';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation, useWorkspace } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * Actions and desk permissions (docs/features/actions-and-permissions.md): tool calls of agents that wait for a
 * person, what a Work Item's agent did, and what a desk lets its agents do per tool.
 */

const keys = {
  itemActions: (workItemId: string) => [...qk.workItem(workItemId), 'actions'] as const,
  deskPermissions: (deskId: string) => [...qk.desk(deskId), 'permissions'] as const,
  deskInbox: (accountId: string, deskId: string) => [...qk.accountInbox(accountId), deskId] as const,
};

const STATUS: Record<ActionStatus, { label: string; tone: 'amber' | 'green' | 'muted' | 'red' | 'outline' }> = {
  pending_approval: { label: t('Waits for you'), tone: 'amber' },
  approved: { label: t('Approved – agent checks it'), tone: 'outline' },
  running: { label: t('Running'), tone: 'outline' },
  succeeded: { label: t('Done'), tone: 'green' },
  failed: { label: t('Failed'), tone: 'red' },
  rejected: { label: t('Rejected'), tone: 'muted' },
  cancelled: { label: t('Cancelled'), tone: 'muted' },
};

export const PERMISSION_LABEL: Record<ToolPermission, string> = { allow: t('Allow'), ask: t('Ask me'), deny: t('Deny') };

/** Groups waiting actions by the run that asked: they are decided together. */
export function groupActions(actions: Action[]): Action[][] {
  const groups = new Map<string, Action[]>();
  for (const a of actions) groups.set(a.groupId, [...(groups.get(a.groupId) ?? []), a]);
  return [...groups.values()];
}

/** One run's waiting tool calls: what the agent wants to do, approve or reject all with one click. */
export function ActionGroupCard({ actions, showItem = true, onDecided }: { actions: Action[]; showItem?: boolean; onDecided?: () => void }) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const first = actions[0];
  const decide = useApiMutation(
    (decision: 'approve' | 'reject') =>
      actions.length > 1
        ? api.actions.decideGroup(first.groupId, { decision, comment: comment.trim() || undefined })
        : api.actions.decide(first.id, { decision, comment: comment.trim() || undefined }),
    {
      invalidate: [
        first.workItemId ? qk.workItem(first.workItemId) : qk.conversationActions(first.conversationId ?? ''),
        qk.overview(first.workspaceId),
      ],
      success: (done) =>
        done.every((a) => a.status === 'rejected')
          ? t('Rejected — the agent will be told.')
          : done.some((a) => a.status === 'approved')
            ? t('Approved — the agent checks it once more, then runs it.')
            : t('Approved and done — the agent continues.'),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['account'] });
        onDecided?.();
      },
    },
  );
  const busy = decide.isPending;
  return (
    <div className="rounded-xl border-2 border-orange-300 bg-orange-50/70 p-4 dark:border-orange-800 dark:bg-orange-950/30">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-300">
          <ShieldAlert className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          {showItem && (
            <div className="min-w-0">
              <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId: first.workspaceId, deskId: first.deskId }} className="text-xs text-muted-foreground hover:underline">
                {first.deskName ?? t('Desk')}
              </Link>
              {first.workItemId ? (
                <Link
                  to="/w/$workspaceId/work-items/$workItemId"
                  params={{ workspaceId: first.workspaceId, workItemId: first.workItemId }}
                  className="block truncate text-sm font-semibold hover:text-primary hover:underline"
                >
                  {first.workItemTitle ?? t('Work Item')}
                </Link>
              ) : (
                <Link
                  to="/w/$workspaceId/conversations/$conversationId"
                  params={{ workspaceId: first.workspaceId, conversationId: first.conversationId ?? '' }}
                  className="block truncate text-sm font-semibold hover:text-primary hover:underline"
                >
                  {first.conversationTitle ?? t('Chat')}
                </Link>
              )}
            </div>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">
            {actions.length > 1
              ? tr('{agent} wants to make {n} calls – nothing has run yet · asked {time}', {
                  agent: first.agentName ?? t('The agent'),
                  n: actions.length,
                  time: <RelativeTime value={first.requestedAt} />,
                })
              : tr('{agent} wants to make this call – nothing has run yet · asked {time}', {
                  agent: first.agentName ?? t('The agent'),
                  time: <RelativeTime value={first.requestedAt} />,
                })}
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {actions.map((a) => (
              <ActionCall key={a.id} action={a} />
            ))}
          </ul>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('Comment for the agent (optional)')}
            rows={2}
            className="mt-3 bg-card"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => decide.mutate('approve')} loading={busy && decide.variables === 'approve'} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-600/90">
              <Check /> {actions.length > 1 ? t('Approve all {n}', { n: actions.length }) : t('Approve')}
            </Button>
            <Button variant="outline" onClick={() => decide.mutate('reject')} loading={busy && decide.variables === 'reject'} disabled={busy}>
              <X /> {actions.length > 1 ? t('Reject all') : t('Reject')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One waiting call: the agent's own words on what it changes, what the tool does, and its arguments to check. Long
 * arguments of a call the agent explained are folded, so the card does not push the work below out of sight.
 */
function ActionCall({ action }: { action: Action }) {
  const [raw, setRaw] = useState(false);
  const long = !!action.explanation?.trim() && JSON.stringify(action.arguments ?? {}).length > 600;
  const [shown, setShown] = useState(!long);
  return (
    <li className="rounded-lg border bg-card px-3 py-2.5">
      <div className="flex items-baseline gap-2 text-sm">
        <span className="min-w-0 truncate font-medium">{action.toolTitle ?? <span className="font-mono">{action.tool}</span>}</span>
        {action.toolTitle && <span className="hidden shrink-0 font-mono text-xs text-muted-foreground sm:inline">{action.tool}</span>}
        <span className="truncate text-xs text-muted-foreground">· {action.connectionName}</span>
        <button type="button" className="ml-auto shrink-0 cursor-pointer text-xs text-muted-foreground hover:text-foreground" onClick={() => setRaw((r) => !r)}>
          {raw ? t('Readable') : 'JSON'}
        </button>
      </div>
      {action.toolDescription && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" title={action.toolDescription}>{action.toolDescription}</p>}
      {action.explanation && (
        <blockquote className="mt-2.5 border-l-2 border-orange-400 pl-3 text-sm whitespace-pre-wrap">
          {action.explanation}
          <footer className="mt-0.5 text-xs text-muted-foreground">— {action.agentName ?? t('the agent')}</footer>
        </blockquote>
      )}
      {shown ? (
        <div className="mt-2.5">{raw ? <JsonBlock value={action.arguments} maxHeight="max-h-64" /> : <ActionArguments args={action.arguments} info={action.argumentInfo} />}</div>
      ) : null}
      {long && (
        <button
          type="button"
          aria-expanded={shown}
          onClick={() => setShown((v) => !v)}
          className="mt-2 cursor-pointer text-xs font-medium text-primary hover:underline"
        >
          {shown ? t('Hide what it sends') : t('Show what it sends')}
        </button>
      )}
    </li>
  );
}

/**
 * A call the agent asked for in a chat that writes (docs/features/conversations.md#writing-in-a-chat): the approval card
 * while it waits — once per run, for all its waiting calls — then a line with what became of it.
 */
export function ChatAction({ conversationId, actionId }: { conversationId: string; actionId: string }) {
  const actions = useQuery({ queryKey: qk.conversationActions(conversationId), queryFn: () => api.conversations.actions(conversationId) });
  const action = actions.data?.find((a) => a.id === actionId);
  if (!action) return null;
  if (action.status === 'pending_approval') {
    const group = (actions.data ?? []).filter((a) => a.groupId === action.groupId && a.status === 'pending_approval');
    if (group[0]?.id !== action.id) return null;
    return <ActionGroupCard actions={group} showItem={false} />;
  }
  const status = STATUS[action.status];
  return (
    <div className="flex justify-center">
      <div className="inline-flex max-w-[90%] items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="size-3.5 shrink-0" />
        <span className="truncate">
          <span className="font-mono">{action.tool}</span> · {action.connectionName}
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
    </div>
  );
}

/** Only the calls of a Work Item that wait for a person, to decide where the item is shown (VICKI's chat). */
export function WaitingActions({ workItemId }: { workItemId: string }) {
  const actions = useQuery({ queryKey: keys.itemActions(workItemId), queryFn: () => api.actions.forWorkItem(workItemId) });
  const waiting = (actions.data ?? []).filter((a) => a.status === 'pending_approval');
  return (
    <>
      {groupActions(waiting).map((group) => (
        <ActionGroupCard key={group[0].groupId} actions={group} showItem={false} />
      ))}
    </>
  );
}

/** Work Item page: waiting calls on top, then everything the agent did through writing tools. */
export function WorkItemActions({ workItemId }: { workItemId: string }) {
  const actions = useQuery({ queryKey: keys.itemActions(workItemId), queryFn: () => api.actions.forWorkItem(workItemId) });
  const list = actions.data ?? [];
  if (!list.length) return null;
  const waiting = list.filter((a) => a.status === 'pending_approval');
  return (
    <div className="flex flex-col gap-3">
      {groupActions(waiting).map((group) => (
        <ActionGroupCard key={group[0].groupId} actions={group} showItem={false} />
      ))}
      <Section icon={<ShieldCheck />} title={t('Actions')} description={t('Calls of writing tools, with who approved them and what came back.')}>
        <ul className="-mx-5 -mb-5 divide-y border-t">
          {list.map((a) => (
            <ActionRow key={a.id} action={a} />
          ))}
        </ul>
      </Section>
    </div>
  );
}

function ActionRow({ action: a }: { action: Action }) {
  const [open, setOpen] = useState(false);
  const status = STATUS[a.status];
  return (
    <li className="px-5 py-2.5 text-sm">
      <button type="button" className="flex w-full cursor-pointer flex-wrap items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        <span className="font-mono font-medium">{a.tool}</span>
        <span className="text-xs text-muted-foreground">{a.connectionName}</span>
        <Badge tone={status.tone}>{status.label}</Badge>
        <span className="ml-auto text-xs text-muted-foreground">
          <RelativeTime value={a.finishedAt ?? a.decidedAt ?? a.requestedAt} />
        </span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2 text-xs">
          {a.explanation && <p className="border-l-2 pl-3 text-sm whitespace-pre-wrap">{a.explanation}</p>}
          <ActionArguments args={a.arguments} info={a.argumentInfo} />
          {a.permission === 'ask' && a.decidedAt && (
            <p className="text-muted-foreground">
              {a.status === 'rejected'
                ? a.decidedByName
                  ? t('Rejected by {name}', { name: a.decidedByName })
                  : t('Rejected by a person')
                : a.decidedByName
                  ? t('Approved by {name}', { name: a.decidedByName })
                  : t('Approved by a person')}
              {a.comment ? ` ${t('– “{comment}”', { comment: a.comment })}` : ''}
            </p>
          )}
          {a.result && <pre className="max-h-48 overflow-auto rounded-md bg-muted/60 p-2 whitespace-pre-wrap">{a.result}</pre>}
          {a.error && <pre className="max-h-48 overflow-auto rounded-md bg-destructive/10 p-2 whitespace-pre-wrap text-destructive">{a.error}</pre>}
        </div>
      )}
    </li>
  );
}

/**
 * Desk page: "Needs you" of this desk – what only a person can do and tool calls that wait; questions and approvals
 * stay in the global inbox.
 */
export function DeskNeedsYou({ workspaceId, deskId }: { workspaceId: string; deskId: string }) {
  const accountId = useWorkspace(workspaceId).data?.accountId ?? '';
  const inbox = useQuery({
    queryKey: keys.deskInbox(accountId, deskId),
    queryFn: () => api.accounts.inbox(accountId, deskId),
    enabled: !!accountId,
    refetchInterval: 30_000,
  });
  const tasks = (inbox.data?.approvals ?? []).filter(isPersonTask);
  const groups = groupActions(inbox.data?.actions ?? []);
  if (!groups.length && !tasks.length) return null;
  return (
    <Section
      icon={<ShieldAlert />}
      title={t('Needs you')}
      description={tasks.length ? t('What only you can do for this desk, and calls that wait for your approval.') : t("Calls of this desk's agents that wait for your approval.")}
    >
      <div className="flex flex-col gap-3">
        {tasks.map((a) => (
          <PersonTaskCard key={a.id} approval={a} workspaceId={workspaceId} onDecided={() => void inbox.refetch()} />
        ))}
        {groups.map((group) => (
          <ActionGroupCard key={group[0].groupId} actions={group} />
        ))}
      </div>
    </Section>
  );
}

/** Desk page: allow / ask / deny per MCP tool. */
export function DeskPermissions({ deskId }: { deskId: string }) {
  const permissions = useQuery({ queryKey: keys.deskPermissions(deskId), queryFn: () => api.actions.permissions(deskId) });
  const queryClient = useQueryClient();
  const set = useApiMutation(
    (input: { p: DeskToolPermission; permission: ToolPermission | null }) =>
      api.actions.setPermission(deskId, { connectionId: input.p.connectionId, tool: input.p.tool, permission: input.permission }),
    { success: t('Permission saved'), onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.deskPermissions(deskId) }) },
  );
  const byConnection = new Map<string, DeskToolPermission[]>();
  for (const p of permissions.data ?? []) byConnection.set(p.connectionName, [...(byConnection.get(p.connectionName) ?? []), p]);

  return (
    <Section
      icon={<ShieldCheck />}
      title={t('Permissions')}
      description={t('What the agents of this desk may do with each tool. Deskworks checks it before every call. Default: reading allowed, writing asks you, tools that don’t say whether they write are denied.')}
    >
      {permissions.isPending ? (
        <LoadingRows rows={3} />
      ) : permissions.isError ? (
        <ErrorState error={permissions.error} onRetry={() => void permissions.refetch()} />
      ) : byConnection.size === 0 ? (
        <EmptyState compact title={t('No tools yet')} description={t('Add an MCP connection to this desk; its tools show up here.')} />
      ) : (
        <div className="-mx-5 -mb-5 divide-y border-t">
          {[...byConnection.entries()].map(([connection, tools]) => (
            <div key={connection} className="px-5 py-3">
              <div className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">{connection}</div>
              <ul className="flex flex-col gap-1.5">
                {tools.map((p) => (
                  <li key={p.tool} className="flex items-center gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono">{p.tool}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{p.readOnly ? t('reads') : p.annotated ? t('writes') : t('doesn’t say')}</span>
                    </div>
                    <Select
                      value={p.custom ? p.permission : 'default'}
                      disabled={set.isPending}
                      onValueChange={(v) => set.mutate({ p, permission: v === 'default' ? null : (v as ToolPermission) })}
                    >
                      <SelectTrigger className="h-8 w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">{t('Default ({permission})', { permission: PERMISSION_LABEL[p.defaultPermission] })}</SelectItem>
                        {TOOL_PERMISSIONS.map((perm) => (
                          <SelectItem key={perm} value={perm}>
                            {PERMISSION_LABEL[perm]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
